import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Bot, PhoneForwarded, Smartphone } from 'lucide-react';
import { authAPI, tenantAPI } from '../../../../api/client';
import { useAuth } from '../../../../context/AuthContext';
import { useTenant } from '../../../../context/TenantContext';
import { apiError } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { ErrorBanner, useBasic } from '../../ui';
import { Card, Field, SectionHead } from '../components/SettingsUI';

/** Settings › Calling Preferences: the number TeleCalX rings for your calls, company call features, and where calls go. */
export default function CallingSection() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { can } = useTenant();
  const { notify } = useBasic();
  const [phone, setPhone] = useState(user.phone || '');
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => tenantAPI.features()
    .then((res) => setFlags(res.data.filter((f) => f.name !== 'registration_enabled')))
    .catch((err) => setError(apiError(err))), []);
  useEffect(() => { load(); }, [load]);

  const savePhone = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await authAPI.updateMe({ phone: phone.trim() });
      await refresh();
      notify(t('Your calling number is saved.'));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const setFlag = async (name, value) => {
    setError('');
    try {
      await tenantAPI.setFeature(name, value === 'inherit' ? null : value === 'true');
      notify(t('Call setting updated.'));
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  return (
    <>
      <SectionHead title={t('Calling Preferences')} subtitle={t('Outbound and inbound call settings.')} />
      <ErrorBanner message={error} />
      <Card title={t('Your Calling Number')} subtitle={t('When you call a customer, TeleCalX rings this phone first, then connects them.')} icon={Smartphone} tone="green">
        <form className="st-inline-form" onSubmit={savePhone}>
          <Field label={t('Phone Number')}><input value={phone} inputMode="tel" maxLength={20} onChange={(e) => setPhone(e.target.value)} /></Field>
          <button className="primary-action" disabled={busy || phone.trim() === (user.phone || '')}>{t('Save')}</button>
        </form>
      </Card>

      <Card title={t('Company Call Features')} subtitle={can('flags.write') ? t('"Default" follows the TeleCalX platform setting.') : t('Only owners and admins can change these.')}>
        <div className="table-scroll">
          <table className="full-page-table st-flags">
            <thead><tr><th>{t('Feature')}</th><th>{t('Platform default')}</th><th>{t('Your company')}</th></tr></thead>
            <tbody>
              {flags?.map((f) => (
                <tr key={f.name}>
                  <td><b className="cell-strong">{f.description || f.name}</b></td>
                  <td>{f.platform_default ? t('On') : t('Off')}</td>
                  <td>
                    <select className="table-select" value={f.overridden ? String(f.enabled) : 'inherit'} disabled={!can('flags.write')}
                      aria-label={f.description || f.name} onChange={(e) => setFlag(f.name, e.target.value)}>
                      <option value="inherit">{t('Default')} ({f.platform_default ? t('On') : t('Off')})</option>
                      <option value="true">{t('On')}</option>
                      <option value="false">{t('Off')}</option>
                    </select>
                  </td>
                </tr>
              ))}
              {flags?.length === 0 && <tr><td colSpan={3} className="empty-state">{t('No configurable features.')}</td></tr>}
              {flags === null && <tr><td colSpan={3} className="empty-state">{t('Loading…')}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title={t('Incoming Calls')} subtitle={t('Choose who answers, office hours and what callers hear.')}>
        <div className="st-action-cards two">
          <button type="button" className="st-action-card" onClick={() => navigate('/phone-numbers')}>
            <span className="st-icon blue"><PhoneForwarded size={18} /></span>
            <span><b>{t('Call Routing')}</b><small>{t('Ring team members, voicemail when nobody answers')}</small></span><ArrowRight size={15} />
          </button>
          <button type="button" className="st-action-card" onClick={() => navigate('/ai-receptionist')}>
            <span className="st-icon purple"><Bot size={18} /></span>
            <span><b>{t('Greeting & Office Hours')}</b><small>{t('What callers hear, and after-hours voicemail')}</small></span><ArrowRight size={15} />
          </button>
        </div>
      </Card>
    </>
  );
}
